// Shared contract for interactive tool calls (askQuestion, …).

export type InteractionKind = "short_text" | "long_text" | "choice";

export interface AskQuestion {
	id: string;
	question: string;
	kind: InteractionKind;
	options?: string[];
	multiSelect?: boolean;
	placeholder?: string;
	optional?: boolean;
}

export interface AskQuestionPayload {
	intro?: string;
	questions: AskQuestion[];
}

/** A question after normalization — currently identical to `AskQuestion`. */
export type InteractionQuestion = AskQuestion;

/** A tool call parsed into a surface-agnostic interaction the UI can render. */
export interface ParsedInteraction {
	toolName: string;
	toolCallId: string;
	/** Default title; a surface may substitute its own copy. */
	header: string;
	intro?: string;
	questions: InteractionQuestion[];
	/** Single choice, single-select → selecting an option can auto-submit. */
	autoAdvance: boolean;
}

/** One resolved answer, as stored in `toolResponses`. */
export interface InteractionAnswer {
	questionId: string;
	value: string | string[];
	/** True when the operator typed a custom answer (the "Other" free text). */
	typed: boolean;
}

/** UI-side answer state: value plus whether it was typed vs selected. */
export interface InteractionAnswerInput {
	value: string | string[];
	typed: boolean;
}

export type InteractionAnswerMap = Record<string, InteractionAnswerInput>;

export interface ToolInteractionSpec {
	toolName: string;
	/** Returns null when the tool-call input isn't a valid request for this tool. */
	parseRequest(input: unknown, toolCallId: string): ParsedInteraction | null;
	/** Header shown on the resolved record. */
	resolvedHeader(): string;
}

// askQuestion

export const ASK_QUESTION_HEADER = "Clerical needs a few details";

export function isAskQuestionPayload(x: unknown): x is AskQuestionPayload {
	if (!x || typeof x !== "object") return false;
	const o = x as Record<string, unknown>;
	if (!Array.isArray(o.questions) || o.questions.length === 0) return false;
	for (const q of o.questions as unknown[]) {
		if (!q || typeof q !== "object") return false;
		const qo = q as Record<string, unknown>;
		if (typeof qo.id !== "string" || qo.id.length === 0) return false;
		if (typeof qo.question !== "string" || qo.question.length === 0)
			return false;
		if (
			qo.kind !== "short_text" &&
			qo.kind !== "long_text" &&
			qo.kind !== "choice"
		) {
			return false;
		}
		if (qo.kind === "choice") {
			if (!Array.isArray(qo.options) || qo.options.length < 2) return false;
		}
	}
	if (o.intro !== undefined && typeof o.intro !== "string") return false;
	return true;
}

function askQuestionAutoAdvance(payload: AskQuestionPayload): boolean {
	if (payload.questions.length !== 1) return false;
	const q = payload.questions[0];
	return q.kind === "choice" && !q.multiSelect;
}

export const askQuestionSpec: ToolInteractionSpec = {
	toolName: "askQuestion",
	parseRequest(input, toolCallId) {
		if (!isAskQuestionPayload(input)) return null;
		return {
			toolName: "askQuestion",
			toolCallId,
			header: ASK_QUESTION_HEADER,
			intro: input.intro,
			questions: input.questions,
			autoAdvance: askQuestionAutoAdvance(input),
		};
	},
	resolvedHeader() {
		return "Questions answered";
	},
};

export const pauseWorkflowSpec: ToolInteractionSpec = {
	toolName: "pauseWorkflow",
	parseRequest(input, toolCallId) {
		if (!isAskQuestionPayload(input)) return null;
		return {
			toolName: "pauseWorkflow",
			toolCallId,
			header: "Workflow needs required input",
			intro: input.intro,
			questions: input.questions,
			autoAdvance: askQuestionAutoAdvance(input),
		};
	},
	resolvedHeader() {
		return "Workflow blocker answered";
	},
};

const legacyPauseWorkflowSpec: ToolInteractionSpec = {
	...pauseWorkflowSpec,
	toolName: "pausePackageWorkflow",
	parseRequest(input, toolCallId) {
		const parsed = pauseWorkflowSpec.parseRequest(input, toolCallId);
		return parsed ? { ...parsed, toolName: "pausePackageWorkflow" } : null;
	},
};

// registry

const SPECS: ToolInteractionSpec[] = [
	askQuestionSpec,
	pauseWorkflowSpec,
	legacyPauseWorkflowSpec,
];

const SPEC_BY_NAME: Record<string, ToolInteractionSpec> = Object.fromEntries(
	SPECS.map((s) => [s.toolName, s]),
);

export function interactionFor(toolName: string): ToolInteractionSpec | null {
	return SPEC_BY_NAME[toolName] ?? null;
}

/** Parse a tool call into a `ParsedInteraction`, or null if not interactive. */
export function parseInteraction(
	toolName: string,
	input: unknown,
	toolCallId: string,
): ParsedInteraction | null {
	return interactionFor(toolName)?.parseRequest(input, toolCallId) ?? null;
}

export function isInteractiveTool(toolName: string): boolean {
	return toolName in SPEC_BY_NAME;
}

// answers

// Model-facing answer text; keep byte-identical.
export function formatAnswerMessage(
	parsed: Pick<ParsedInteraction, "questions">,
	values: Record<string, string | string[]>,
): string {
	const lines = ["Answers to your questions:", ""];
	for (const q of parsed.questions) {
		const a = values[q.id];
		const value = Array.isArray(a) ? a.join(", ") : a;
		if (value && value.trim().length > 0) {
			lines.push(`- ${q.id}: ${value}`);
		} else {
			lines.push(`- ${q.id}: (not provided)`);
		}
	}
	return lines.join("\n");
}

/** Extract just the values from the richer UI answer map. */
export function answerValues(
	answers: InteractionAnswerMap,
): Record<string, string | string[]> {
	const out: Record<string, string | string[]> = {};
	for (const [id, a] of Object.entries(answers)) out[id] = a.value;
	return out;
}

/** Build the structured answers persisted to `toolResponses`. */
export function toStructuredAnswers(
	parsed: Pick<ParsedInteraction, "questions">,
	answers: InteractionAnswerMap,
): InteractionAnswer[] {
	const out: InteractionAnswer[] = [];
	for (const q of parsed.questions) {
		const a = answers[q.id];
		if (!a) continue;
		out.push({ questionId: q.id, value: a.value, typed: a.typed });
	}
	return out;
}

/** True when a question has an acceptable answer (mirrors the card's guard). */
export function isAnswered(
	q: InteractionQuestion,
	value: string | string[] | undefined,
): boolean {
	if (q.kind === "choice" && q.multiSelect) {
		return Array.isArray(value) && value.length > 0;
	}
	return typeof value === "string" && value.trim().length > 0;
}
